import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trash2, Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/contacts")({
  component: ContactsAdmin,
});

interface Submission {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  subject: string | null;
  message: string;
  is_read: boolean;
  created_at: string;
}

function ContactsAdmin() {
  const [items, setItems] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("contact_submissions")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) toast.error(error.message);
    setItems((data ?? []) as Submission[]);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const toggleRead = async (id: string, current: boolean) => {
    const { error } = await supabase
      .from("contact_submissions")
      .update({ is_read: !current })
      .eq("id", id);
    if (error) return toast.error(error.message);
    setItems((arr) => arr.map((x) => (x.id === id ? { ...x, is_read: !current } : x)));
  };

  const remove = async (id: string) => {
    if (!confirm("متأكد؟")) return;
    const { error } = await supabase.from("contact_submissions").delete().eq("id", id);
    if (error) return toast.error(error.message);
    setItems((arr) => arr.filter((x) => x.id !== id));
    toast.success("اتمسحت");
  };

  return (
    <div>
      <h1 className="text-3xl font-extrabold mb-6">Contact Messages</h1>
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : items.length === 0 ? (
        <p className="text-muted-foreground">مفيش رسائل لسه</p>
      ) : (
        <div className="space-y-3">
          {items.map((m) => (
            <Card
              key={m.id}
              className={`rounded-2xl border-0 shadow-soft ${!m.is_read ? "bg-primary/5 border-l-4 border-l-primary" : ""}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold">{m.name}</h3>
                      {!m.is_read && <Badge>New</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {m.email}
                      {m.phone ? ` · ${m.phone}` : ""}
                    </p>
                    {m.subject && <p className="text-sm font-semibold mt-1">{m.subject}</p>}
                    <p className="mt-2 whitespace-pre-wrap text-sm">{m.message}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {new Date(m.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => toggleRead(m.id, m.is_read)}>
                      {m.is_read ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => remove(m.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
